import matchesActiveIf, { KEY_MAPPING } from 'Pods/ThreeDStudio/Hooks/matchesActiveIf'
import { MODEL_SOURCES } from 'Pods/ThreeDStudio/Services/Constants/modelSources'

import { EMPTY_OBJECT } from 'Utils/stableDefaults'

const resolveValue = (condition, currentValue) => {
  if ('const' in condition) {
    return condition.const === currentValue ? undefined : condition.const
  }

  if ('enum' in condition) {
    return condition.enum.includes(currentValue) ? undefined : condition.enum[0]
  }

  return undefined
}

/**
 * Builds the model settings patch that makes a provider's schema eligible for
 * the current action - the settings the engine does not support, reset to the
 * value its `active_if` requires. Used when the user picks an engine while an
 * additional setting it cannot run is on: the engine wins, the setting resets.
 *
 * Only the `active_if.properties` form is resolved (no generation schema uses
 * the `allOf` form, which has no single satisfying value); a provider already
 * eligible, or one with no schema for the action, yields an empty patch.
 *
 * @param {string} action - Generation action, e.g. ACTION.IMAGE_TO_3D.
 * @param {string} provider - Provider id being selected, e.g. PROVIDER.TENCENT.
 * @param {Object} modelSettings - Current model settings slice.
 * @returns {Object} Patch keyed by store keys (e.g. `isSmartLowPoly`), empty when nothing to reset.
 */
export const getProviderSettingsPatch = (action, provider, modelSettings) => {
  const candidates = MODEL_SOURCES.filter((schema) => (
    schema.action === action && schema.provider === provider
  ))
  if (candidates.length === 0 || candidates.some(schema => matchesActiveIf(schema, modelSettings))) {
    return EMPTY_OBJECT
  }

  // Providers ship a single schema per generation action; MODEL_SOURCES order
  // decides which one leads when that ever stops being true.
  const properties = candidates[0].active_if?.properties || {}

  return Object.entries(properties).reduce((patch, [schemaKey, condition]) => {
    const storeKey = KEY_MAPPING[schemaKey]
    if (!storeKey) {
      return patch
    }

    const value = resolveValue(condition, modelSettings[storeKey])

    return value === undefined ? patch : { ...patch, [storeKey]: value }
  }, {})
}
